import { useState, useMemo, useEffect, ReactNode } from 'react';
import type { VirtualElement } from '@popperjs/core';
import { refHandler } from 'shared/lib/helper-functions';
import { AddComment } from 'app/components/editor/sidebar/comments/AddComment';
import { AddTag } from 'app/components/editor/sidebar/tags/AddTag';
import { StyledElement } from 'slate/components/StyledElement';
import { AddStyledParagraph } from 'app/components/editor/sidebar/styledparagraph/AddStyledParagraph';
import { centeredPopperElement } from 'shared/lib/helper-functions';
import { usePopper } from 'react-popper';
import { Portal } from 'app/components/editor/sidebar/Portal';
import { useEditorMode } from 'app/state/contexts/EditorContext';
import { useSlateSelector, RenderElementProps } from 'slate-react';
import { Descendant, Editor, Element } from 'slate';
import { isInsideStyledParagraph } from '../extensions/styled-paragraph';
import { getClosestElementByType } from '../utils';
import { useEditorCaret } from 'app/state/contexts/EditorUiContext';

type StyledParagraphState = { inStyled: boolean; hasClosest: boolean; closestUuid?: string };

const cachePerChange = <T,>(compute: (editor: Editor) => T) => {
  let last: { selection: Editor['selection']; children: Descendant[]; value: T } | null = null;
  return (editor: Editor): T => {
    if (last && last.selection === editor.selection && last.children === editor.children) return last.value;
    const value = compute(editor);
    last = { selection: editor.selection, children: editor.children, value };
    return value;
  };
};

const selectStyledParagraph = cachePerChange((editor): StyledParagraphState => {
  const styled = isInsideStyledParagraph(editor);
  const closest = getClosestElementByType(styled, 'paragraph');
  return { inStyled: !!styled, hasClosest: !!closest, closestUuid: closest?.uuid };
});

const sameStyledParagraph = (a: StyledParagraphState | null, b: StyledParagraphState): boolean =>
  !!a && a.inStyled === b.inStyled && a.hasClosest === b.hasClosest && a.closestUuid === b.closestUuid;

const selectMultipleParagraphsSelected = cachePerChange((editor): boolean => {
  if (!editor.selection) return false;
  return (
    Array.from(
      Editor.nodes(editor, {
        at: editor.selection,
        match: (n) => Element.isElement(n) && Editor.isBlock(editor, n) && n.type === 'paragraph',
      })
    ).length > 1
  );
});

type ParagraphProps = RenderElementProps & {
  withComments?: boolean;
  withStyledParagrah?: boolean;
  withTags?: boolean;
};

export const Paragraph = ({ element, attributes, children, withComments, withStyledParagrah, withTags }: ParagraphProps) => {
  const [ref, setRef] = useState<HTMLElement | null>(null);
  const [show, setShow] = useState(true);
  const selectionId = useEditorCaret().selectionId;
  const { isTaggingMode } = useEditorMode();
  const styledParagraph = useSlateSelector(selectStyledParagraph, sameStyledParagraph);
  const isInStyledParagraph = styledParagraph.inStyled;
  const isMultipleParagraphsSelected = useSlateSelector(selectMultipleParagraphsSelected);

  useEffect(() => {
    if (styledParagraph.inStyled && styledParagraph.hasClosest) {
      setShow(styledParagraph.closestUuid === element.uuid);
      return;
    }
    if (!selectionId) {
      setShow(false);
      return;
    }

    setShow(selectionId === element.uuid);
  }, [styledParagraph, element.uuid, selectionId]);
  const anchor = `anchor-${element.uuid}`;

  return (
    <div {...attributes} ref={refHandler(attributes, setRef as (ref: unknown) => void)}>
      <a contentEditable="false" href={`#${anchor}`} id={anchor}></a>

      <SeletedTextWrapper popperRef={ref} show={show}>
        <>
          {withComments && !isMultipleParagraphsSelected && ref ? <AddComment element={element} /> : null}
          {withStyledParagrah && !isTaggingMode && ref ? <AddStyledParagraph isInsideStyledParagraph={isInStyledParagraph} /> : null}
          {withTags && isTaggingMode && ref && element.uuid ? <AddTag element={{ uuid: element.uuid }} /> : null}
        </>
      </SeletedTextWrapper>

      <StyledElement element={element} attributes={attributes} children={children} />
    </div>
  );
};

type SeletedTextWrapperProps = {
  children: ReactNode;
  popperRef: HTMLElement | null;
  show: boolean;
};

export const SeletedTextWrapper = ({ children, popperRef, show }: SeletedTextWrapperProps) => {
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const referenceElement = useMemo(() => centeredPopperElement(popperRef), [popperRef]);
  const { styles, attributes } = usePopper(referenceElement as VirtualElement, popperElement, {
    placement: 'right-end',
  });
  return (
    <>
      {show && (
        <Portal>
          <div ref={setPopperElement} style={{ ...styles.popper, gap: '2px', display: 'flex', flexDirection: 'column' }} {...attributes.popper}>
            {children}
          </div>
        </Portal>
      )}
    </>
  );
};
